import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { useResource } from '../../lib/useResource';
import { StateCard } from '../../components/StateCard';
import { Empty, GameTile, SiteHead } from '../../components/site';
import type { GameCard } from '../../components/site';
import type { ApiEnvelope } from '../../lib/types';

const SORTS = [
  { value: 'newest', label: '최신 출시순' },
  { value: 'name', label: '이름순' },
  { value: 'oldest', label: '오래된 순' },
];

/**
 * P002 · P017–P020 — catalogue views.
 *
 * One registry, filtered. The platform pages do not keep their own copy of a game.
 */
/** Page copy lives here, in Korean, written for players. The design registry's
 *  `rule` field is an internal note and must never reach a public screen. */
const PAGE_COPY: Record<string, { title: string; lead: string }> = {
  P002: { title: '전체 게임', lead: '플랫폼과 장르별로 게임을 찾아보세요.' },
  P017: { title: '모바일 게임', lead: 'iOS와 Android에서 즐길 게임을 찾아보세요.' },
  P018: { title: '로블록스 게임', lead: '로블록스에서 즐길 게임을 찾아보세요.' },
  P019: { title: 'PC 게임', lead: 'Steam과 PC에서 즐길 게임을 찾아보세요.' },
  P020: { title: '출시 예정', lead: '출시를 앞둔 게임을 확인해 보세요.' },
};

export function PublicGames({
  pageId,
  platformFilter,
  lifecycleFilter,
  lead,
}: {
  pageId: string;
  platformFilter?: string[];
  lifecycleFilter?: string;
  lead?: string;
}) {
  const copy = PAGE_COPY[pageId] ?? PAGE_COPY.P002;
  const [searchParams, setSearchParams] = useSearchParams();

  const term = searchParams.get('q') ?? '';
  const genre = searchParams.get('genre') ?? '';
  const sort = searchParams.get('sort') ?? 'newest';

  const games = useResource<GameCard[]>(
    (signal) => api.public('/api/v1/games', {
      signal,
      query: { q: term, genre, sort, per_page: 60, lifecycle: lifecycleFilter },
    }) as Promise<ApiEnvelope<GameCard[]>>,
    [term, genre, sort, lifecycleFilter],
    // An empty list is a state this screen draws itself, with copy that fits the
    // filter that produced it; the generic EMPTY card would replace the filters too.
    { emptyWhen: () => false },
  );

  const visible = useMemo(() => {
    if (!games.data) return [];
    if (!platformFilter) return games.data;
    return games.data.filter((game) => game.platforms.some((platform) => platformFilter.includes(platform)));
  }, [games.data, platformFilter]);

  const genres = useMemo(
    () => Array.from(new Set((games.data ?? []).map((game) => game.genre).filter(Boolean))) as string[],
    [games.data],
  );

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next);
  };

  const live = visible.filter((game) => game.lifecycle === 'live');
  const upcoming = visible.filter((game) => game.lifecycle !== 'live');

  return (
    <>
      <SiteHead
        eyebrow="게임"
        title={copy.title}
        lead={lead ?? copy.lead}
        meta={
          games.loaded && visible.length > 0
            ? <span>{visible.length}편 · 서비스 중 {live.length} · 출시 예정 {upcoming.length}</span>
            : undefined
        }
      />

      <div className="filters" role="search">
        <input
          type="search"
          aria-label="게임 검색"
          placeholder="게임 이름으로 검색"
          key={term}
          defaultValue={term}
          onKeyDown={(event) => {
            if (event.key === 'Enter') setParam('q', (event.target as HTMLInputElement).value);
          }}
          style={{ minWidth: 220 }}
        />
        <select aria-label="장르" value={genre} onChange={(event) => setParam('genre', event.target.value)}>
          <option value="">모든 장르</option>
          {genres.map((value) => <option key={value} value={value}>{value}</option>)}
        </select>
        <select aria-label="정렬" value={sort} onChange={(event) => setParam('sort', event.target.value)}>
          {SORTS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
        {(term || genre) && (
          <button type="button" className="btn" onClick={() => setSearchParams(new URLSearchParams())}>
            필터 초기화
          </button>
        )}
      </div>

      {/* Emptiness is drawn below, with copy that matches the filter that caused
          it; handing EMPTY to StateCard would replace this whole block. */}
      <StateCard state={games.state} error={games.error} onRetry={games.reload}>
        {live.length > 0 && (
          <div className="card-grid" style={{ marginBottom: upcoming.length ? 48 : 0 }}>
            {live.map((game) => <GameTile key={game.id} game={game} />)}
          </div>
        )}

        {upcoming.length > 0 && (
          <>
            {live.length > 0 && (
              <h2 className="section__title" style={{ marginBottom: 16 }}>출시 예정</h2>
            )}
            <div className="card-grid">
              {upcoming.map((game) => <GameTile key={game.id} game={game} />)}
            </div>
          </>
        )}

        {games.loaded && visible.length === 0 && (
          <Empty
            title={term || genre ? '검색 결과가 없어요' : '공개된 게임이 아직 없어요'}
            body={term || genre ? '다른 검색어나 조건으로 찾아보세요.' : '게임이 공개되면 이곳에서 확인할 수 있어요.'}
          />
        )}
      </StateCard>
    </>
  );
}
